import { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useEncryptedParams } from "../../hooks/useEncryptedParams";
import apiClient from "../../services/apiClient";
import countryService from "../../services/countryService";
import { normalizeApiError } from "../../utils/apiError";
import Button from "../../components/common/Button";
import Input from "../../components/common/Input";
import TextArea from "../../components/common/TextArea";
import Dropdown from "../../components/common/Dropdown";
import ErrorAlert from "../../components/common/ErrorAlert";
import alertService from "../../utils/alertService";
import { useMasterModals } from "../../hooks/useMasterModals";

function CustomerFormPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useEncryptedParams();

  // --- States ---
  const [formData, setFormData] = useState({
    customerCode: "",
    customerName: "",
    mobileNo: "",
    email: "",
    companyName: "",
    countryId: "",
    address: "",
    isActive: true,
  });
  const [countryOptions, setCountryOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isLoadingEdit, setIsLoadingEdit] = useState(false);
  const [formError, setFormError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const isEditPage = Boolean(id);
  const formMode = isEditPage ? "edit" : "create";

  const basePath = useMemo(() => {
    if (location.pathname.includes("/settings-master/customer-master"))
      return "/settings-master/customer-master";
    return "/customer-master";
  }, [location.pathname]);

  const loadCountries = useCallback(async () => {
    try {
      const countries = await countryService.getActiveCountries();
      setCountryOptions(
        countries.map((c) => ({
          label: c.countryName || c.name,
          value: c.countryId || c.id,
        }))
      );
    } catch (err) {
      console.error("Failed to load countries", err);
    }
  }, []);

  const { openModal, renderModals } = useMasterModals({
    country: loadCountries,
  });

  useEffect(() => {
    loadCountries();
  }, [loadCountries]);

  // --- Initial Load for Edit ---
  useEffect(() => {
    if (!isEditPage || !id) return;

    let isMounted = true;
    const loadEditData = async () => {
      setIsLoadingEdit(true);
      setFormError(null);
      try {
        const response = await apiClient.get(`/customer-master/${id}`);
        const payload = response?.data || {};
        const data = payload?.data || payload || {};
        if (!isMounted) return;
        setFormData({
          customerCode: data.customerCode || "",
          customerName: data.customerName || "",
          mobileNo: data.mobileNo || "",
          email: data.email || "",
          companyName: data.companyName || "",
          countryId: data.countryId || data.country?.countryId || "",
          address: data.address || "",
          isActive: data.isActive !== false,
        });
      } catch (err) {
        if (!isMounted) return;
        const normalized = normalizeApiError(err);
        setFormError(normalized.message);
      } finally {
        if (isMounted) setIsLoadingEdit(false);
      }
    };

    loadEditData();
    return () => {
      isMounted = false;
    };
  }, [id, isEditPage]);

  // --- Handlers ---
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validate = () => {
    const errors = {};
    // if (!formData.customerCode.trim()) {
    //   errors.customerCode = "Customer Code is required";
    // } else {
    //   // Alphanumeric, hyphen, and underscore only
    //   const codePattern = /^[a-zA-Z0-9-_]+$/;
    //   if (!codePattern.test(formData.customerCode.trim())) {
    //     errors.customerCode =
    //       "Only alphanumeric, hyphen (-), and underscore (_) are allowed";
    //   }
    // }

    if (!formData.companyName.trim())
      errors.companyName = "Company Name is required";
    if (!formData.countryId)
      errors.countryId = "Country is required";
    if (!formData.address.trim()) errors.address = "Address is required";

    // Email Validation
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.email.trim() && !emailPattern.test(formData.email.trim())) {
      errors.email = "Invalid email format";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async (e) => {
    if (e) e.preventDefault();
    if (!validate()) return;

    setIsSaving(true);
    setFormError(null);
    try {
      const payload = { ...formData };
      if (formMode === "edit" && id) {
        await apiClient.patch(`/customer-master/${id}`, payload);
        alertService.success("Customer updated successfully.");
      } else {
        await apiClient.post("/customer-master", payload);
        alertService.success("Customer created successfully.");
      }
      navigate(basePath);
    } catch (err) {
      const normalized = normalizeApiError(err);
      setFormError(normalized.message);
      alertService.error(normalized.message);
    } finally {
      setIsSaving(false);
    }
  };

  const notFound =
    formMode === "edit" &&
    !isLoadingEdit &&
    !formData.customerCode &&
    !formData.customerName &&
    !formData.companyName &&
    !formError;

  return (
    <div className="space-y-3 max-w-6xl mx-auto">
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-800 dark:text-white uppercase tracking-tight">
            {formMode === "edit" ? "Edit" : "Create"}{" "}
            <span className="text-primary-600">Customer</span>
          </h1>
          <p className="text-xs font-bold text-slate-500 uppercase tracking-widest mt-1">
            Settings Master / Customer / {formMode === "edit" ? "Edit" : "New"}
          </p>
        </div>
        <Button variant="secondary" onClick={() => navigate(basePath)}>
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
            />
          </svg>
          Back to List
        </Button>
      </div>

      {formError ? <ErrorAlert message={formError} /> : null}

      {notFound ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:border-rose-800">
          Record not found.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900 overflow-hidden">
          <form onSubmit={handleSave}>
            <table className="w-full border-collapse">
              <tbody>
                {/* --- Customer Name --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Customer Name
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="customerName"
                      value={formData.customerName}
                      onChange={handleInputChange}
                      error={formErrors.customerName}
                      placeholder="e.g. John Doe"
                    />
                  </td>
                </tr>

                {/* --- Customer Code --- 
                 <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Customer Code <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="customerCode"
                      value={formData.customerCode}
                      onChange={handleInputChange}
                      error={formErrors.customerCode}
                      placeholder="e.g. CUST-001"
                    />
                  </td>
                </tr> */}

                {/* --- Company Name --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Company Name <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleInputChange}
                      error={formErrors.companyName}
                      placeholder="e.g. Doe Enterprises"
                    />
                  </td>
                </tr>

                {/* --- Country --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Country <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <Dropdown
                      name="countryId"
                      value={formData.countryId}
                      onChange={handleInputChange}
                      options={countryOptions}
                      placeholder="Select Country"
                      error={formErrors.countryId}
                      onAdd={() => openModal("country")}
                      addLabel="Add New Country"
                    />
                  </td>
                </tr>

                {/* --- Email --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Email Address
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      error={formErrors.email}
                      placeholder="john@example.com"
                    />
                  </td>
                </tr>

                {/* --- Mobile No --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Mobile Number
                  </td>
                  <td className="py-3 px-6">
                    <Input
                      name="mobileNo"
                      value={formData.mobileNo}
                      onChange={handleInputChange}
                      error={formErrors.mobileNo}
                      placeholder="e.g. 1234567890"
                    />
                  </td>
                </tr>

                {/* --- Address --- */}
                <tr className="border-b border-slate-100 dark:border-slate-800">
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Address <span className="text-danger">*</span>
                  </td>
                  <td className="py-3 px-6">
                    <TextArea
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      error={formErrors.address}
                      placeholder="Enter full address..."
                      rows={3}
                    />
                  </td>
                </tr>

                {/* --- Status --- */}
                <tr>
                  <td className="w-1/3 py-4 px-6 text-[11px] font-black uppercase tracking-widest text-slate-500 bg-slate-50/50 dark:bg-slate-800/50">
                    Status
                  </td>
                  <td className="py-3 px-6">
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={formData.isActive}
                        onChange={handleInputChange}
                        className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-600/20"
                      />
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase">
                        {formData.isActive ? "Active" : "Inactive"}
                      </span>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-slate-50 dark:bg-slate-800/30 p-6 flex items-center gap-3">
              <Button
                type="submit"
                variant="primary"
                isLoading={isSaving}
                className="px-8 shadow-lg shadow-primary-500/20"
              >
                {formMode === "edit" ? "Update Customer" : "Save Customer"}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => navigate(basePath)}
                disabled={isSaving}
                className="px-6"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      {renderModals()}
    </div>
  );
}

export default CustomerFormPage;
